import { faCommentDots } from "@fortawesome/free-solid-svg-icons/faCommentDots";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { ConversationComment } from "../../../../../model/integration/dto/meta/MetaConversationComment";
import { ThemeType } from "../../../../../theme/Theme";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";
import { standardDate } from "../../../../../utils/Date";
import { getTaskPriorityColor } from "../../../../../utils/taskUtils";
import { WACommentPriorityTag } from "./WACommentPriorityTag";
import { WACommentResolvedChip } from "./WACommentResolvedChip";
import { WAUserAvatar } from "../components/WAUserAvatar";

const Row = styled(Flex)`
  justify-content: center;
  margin: ${Spacing.s} 0;
`;

const Card = styled.button<{ $accent: keyof ThemeType; $resolved: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  width: min(32rem, 80%);
  padding: ${Spacing.s} ${Spacing.m};
  text-align: start;
  cursor: pointer;
  font: inherit;
  color: inherit;
  border: 1px dashed ${({ theme }) => theme.darkSub};
  border-inline-start: 3px solid ${({ theme, $accent }) => theme[$accent]};
  border-radius: ${Rounded.lg};
  background-color: ${({ theme }) => theme.lightSub};
  opacity: ${({ $resolved }) => ($resolved ? 0.6 : 1)};

  &:hover {
    background-color: ${({ theme }) => theme.sub};
  }
`;

const Body = styled(Text)`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  unicode-bidi: plaintext;
  line-height: 1.4;
`;

type Props = {
  comment: ConversationComment;
  /** Opens the chat's comments */
  onOpen?: () => void;
};

/** A comment as it sits in the thread, at the point it was written.
 *
 * Styled apart from both bubbles - dashed, and on neither side - so nobody
 * mistakes it for something the customer said or was sent.
 */
export const WACommentNote: React.FC<Props> = ({ comment, onOpen }) => {
  const { t } = useTranslation();

  const resolved = Boolean(comment.resolvedAt);

  return (
    <Row>
      <Card
        type="button"
        onClick={onOpen}
        $accent={getTaskPriorityColor(comment.priority) as keyof ThemeType}
        $resolved={resolved}
        title={t("integration.whatsapp.meta.comments.internal")}
      >
        <Flex alignItems="center" gap="s" flexWrap="wrap">
          <AtiraIcon icon={faCommentDots} size="sm" color="darkerSub" />
          <WAUserAvatar user={comment.author} size="1.25rem" />
          <Text fontSize="s" fontWeight="bold">
            {comment.author?.name ||
              t("integration.whatsapp.meta.comments.unknownAuthor")}
          </Text>
          <WACommentPriorityTag priority={comment.priority} />

          {resolved ? <WACommentResolvedChip comment={comment} /> : null}
        </Flex>

        <Body fontSize="m">{comment.body}</Body>

        <Text fontSize="xs" color="darkerSub">
          {standardDate<string>(comment.createdAt, undefined, true)}
          {comment.editedAt
            ? ` · ${t("integration.whatsapp.meta.comments.edited")}`
            : ""}
        </Text>
      </Card>
    </Row>
  );
};
